import { useEffect, useState } from "react";
import { X, User, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { getProfile, saveProfile, clearProfile, isProfileComplete } from "@/lib/profile";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function ProfileModal({ open, onClose, onSaved }) {
  const [p, setP] = useState(getProfile());
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (open) setP(getProfile());
  }, [open]);

  const update = (field, value) => setP((s) => ({ ...s, [field]: value }));

  const handleSave = () => {
    if (!isProfileComplete(p)) {
      toast.error("Merci de renseigner au moins nom, prénom, adresse, code postal et ville.");
      return;
    }
    saveProfile(p);
    toast.success("Coordonnées enregistrées — Zerux les utilisera automatiquement.");
    if (onSaved) onSaved();
    onClose();
  };

  const handleClear = () => {
    setConfirmClear(true);
  };

  const doClear = () => {
    clearProfile();
    setP(getProfile());
    toast.success("Coordonnées effacées.");
    setConfirmClear(false);
    if (onSaved) onSaved();
  };

  if (!open) return null;

  return (
    <div
      data-testid="profile-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 fade-up"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col rounded-md"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.3)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
        }}
      >
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User size={20} strokeWidth={1.4} style={{ color: "#D4AF37" }} />
            <div>
              <div className="font-serif-display text-xl tracking-tight">Mes coordonnées</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Enregistré uniquement dans votre navigateur
              </div>
            </div>
          </div>
          <button
            data-testid="profile-close-button"
            onClick={onClose}
            className="text-zinc-500 hover:text-[#D4AF37] transition-colors"
          >
            <X size={20} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <p className="text-[12px] text-zinc-400 leading-relaxed">
            Renseignez vos coordonnées une seule fois. Zerux les insère automatiquement en haut
            de chaque lettre — plus besoin de les retaper à chaque demande.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Civilité" placeholder="Monsieur / Madame" value={p.civilite} onChange={(v) => update("civilite", v)} testId="profile-civilite" />
            <div />
            <Field label="Prénom *" placeholder="Marie" value={p.prenom} onChange={(v) => update("prenom", v)} testId="profile-prenom" />
            <Field label="Nom *" placeholder="Martin" value={p.nom} onChange={(v) => update("nom", v)} testId="profile-nom" />
          </div>

          <Field label="Adresse *" placeholder="22 rue Pasteur" value={p.adresse} onChange={(v) => update("adresse", v)} testId="profile-adresse" />

          <div className="grid grid-cols-3 gap-3">
            <Field label="Code postal *" placeholder="75015" value={p.codePostal} onChange={(v) => update("codePostal", v)} testId="profile-cp" />
            <div className="col-span-2">
              <Field label="Ville *" placeholder="Paris" value={p.ville} onChange={(v) => update("ville", v)} testId="profile-ville" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone" placeholder="06 12 34 56 78" value={p.telephone} onChange={(v) => update("telephone", v)} testId="profile-tel" />
            <Field label="Email" placeholder="marie.martin@email.fr" value={p.email} onChange={(v) => update("email", v)} testId="profile-email" />
          </div>

          <div
            className="mt-4 px-3 py-2.5 rounded-sm flex items-start gap-2 text-[11px] text-zinc-400 leading-relaxed"
            style={{ background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)" }}
          >
            <ShieldCheck size={14} strokeWidth={1.7} style={{ color: "#D4AF37", marginTop: 1, flexShrink: 0 }} />
            <span>
              <strong className="text-zinc-200">100 % privé.</strong> Vos coordonnées restent sur votre appareil
              (navigateur uniquement). Aucun serveur ne les stocke. Elles ne servent qu&rsquo;à personnaliser vos lettres.
            </span>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-white/5 flex items-center justify-between gap-3">
          <button
            data-testid="profile-clear-button"
            onClick={handleClear}
            className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 transition-colors"
          >
            Effacer
          </button>
          <button
            data-testid="profile-save-button"
            onClick={handleSave}
            className="btn-gold-solid h-10 px-5 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2"
          >
            <Save size={14} strokeWidth={1.8} />
            Enregistrer
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmClear}
        title="Effacer vos coordonnées ?"
        message="Vos informations personnelles seront définitivement supprimées de ce navigateur. Vous devrez les ressaisir pour générer une lettre personnalisée."
        confirmLabel="Effacer"
        cancelLabel="Annuler"
        tone="danger"
        icon="warning"
        onConfirm={doClear}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1 block">{label}</span>
      <input
        data-testid={testId}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full glass-input rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-[#D4AF37]/60"
      />
    </label>
  );
}
